import { useState, useEffect, useRef } from 'react'
import { Building2, Globe, Map, MapPin, Lightbulb, Search } from 'lucide-react'
import { searchLocations, formatLocation, type LocationItem } from '../data/locationsDatabase'

interface LocationAutocompleteProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
}

export function LocationAutocomplete({ 
  value, 
  onChange, 
  placeholder = "e.g., New York City, Paris, Tokyo",
  className = ""
}: LocationAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [suggestions, setSuggestions] = useState<LocationItem[]>([])
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [inputValue, setInputValue] = useState(value)
  const [isLoading, setIsLoading] = useState(false)
  
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  
  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value)
  }, [value])
  
  // Search for suggestions when input changes
  useEffect(() => {
    if (inputValue.length >= 2) {
      setIsLoading(true)
      // Make search async to handle initialization
      searchLocations(inputValue, 8).then(results => {
        setSuggestions(results)
        setIsOpen(results.length > 0)
        setHighlightedIndex(-1)
        setIsLoading(false)
      }).catch(error => {
        console.error('Location search error:', error)
        setSuggestions([])
        setIsOpen(false)
        setIsLoading(false)
      })
    } else {
      setSuggestions([])
      setIsOpen(false)
      setIsLoading(false)
    }
  }, [inputValue])
  
  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
    onChange(newValue)
  }
  
  // Handle suggestion selection
  const handleSuggestionSelect = (location: LocationItem, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    const formattedLocation = formatLocation(location)
    setInputValue(formattedLocation)
    onChange(formattedLocation)
    setIsOpen(false)
    setHighlightedIndex(-1)
    inputRef.current?.blur()
  }
  
  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) return
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        )
        break
        
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1)
        break
        
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0) {
          handleSuggestionSelect(suggestions[highlightedIndex])
        }
        break
        
      case 'Escape':
        setIsOpen(false)
        setHighlightedIndex(-1)
        inputRef.current?.blur()
        break
    }
  }
  
  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ 
          block: 'nearest',
          behavior: 'smooth'
        })
      }
    }
  }, [highlightedIndex])
  
  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (inputRef.current && 
          !inputRef.current.contains(target) && 
          !listRef.current?.contains(target)) {
        setIsOpen(false)
        setHighlightedIndex(-1)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  
  const getLocationIcon = (type: LocationItem['type']) => {
    switch (type) {
      case 'city': return <Building2 className="w-5 h-5 text-blue-500" />
      case 'country': return <Globe className="w-5 h-5 text-green-500" />
      case 'state': return <Map className="w-5 h-5 text-purple-500" />
      default: return <MapPin className="w-5 h-5 text-gray-500" />
    }
  }
  
  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (suggestions.length > 0) {
            setIsOpen(true)
          }
        }}
        placeholder={placeholder}
        className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${className}`}
        autoComplete="off"
      />
      
      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          <ul ref={listRef} className="py-1">
            {suggestions.map((suggestion, index) => (
              <li
                key={`${suggestion.name}-${suggestion.country}-${index}`}
                className={`px-4 py-3 cursor-pointer hover:bg-blue-50 transition-colors ${
                  index === highlightedIndex ? 'bg-blue-50 border-l-2 border-blue-500' : ''
                }`}
                onClick={(e) => handleSuggestionSelect(suggestion, e)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">{getLocationIcon(suggestion.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900 truncate">
                        {suggestion.name}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        suggestion.type === 'city' ? 'bg-blue-100 text-blue-800' :
                        suggestion.type === 'country' ? 'bg-green-100 text-green-800' :
                        suggestion.type === 'state' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {suggestion.type}
                      </span>
                    </div>
                    {suggestion.name !== suggestion.country && (
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500 truncate">
                          {suggestion.country}
                        </p>
                        {suggestion.population && suggestion.population > 100000 && (
                          <span className="text-xs text-gray-400 ml-2">
                            {(suggestion.population / 1000000).toFixed(1)}M people
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          
          {/* Helpful footer */}
          <div className="border-t border-gray-100 px-4 py-2 bg-gray-50">
            <p className="text-xs text-gray-500 flex items-center">
              <Lightbulb className="w-3 h-3 mr-2" />
              Use ↑↓ arrows to navigate, Enter to select, Esc to close
            </p>
          </div>
        </div>
      )}
      
      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="px-4 py-3 text-center text-gray-500">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent mx-auto mb-2"></div>
            <p className="text-sm">Loading locations...</p>
          </div>
        </div>
      )}

      {/* No results message */}
      {isOpen && suggestions.length === 0 && inputValue.length >= 2 && !isLoading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="px-4 py-3 text-center text-gray-500">
            <Search className="w-6 h-6 text-gray-400 mb-2" />
            <p className="text-sm">No locations found for "{inputValue}"</p>
            <p className="text-xs mt-1">Try searching for a city, country, or landmark</p>
          </div>
        </div>
      )}
    </div>
  )
}